import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const MINOR_UNITS_PER_UNIT = 100;

export function formatCurrency(minorUnits: number, symbol = "$"): string {
  return `${symbol}${(minorUnits / MINOR_UNITS_PER_UNIT).toFixed(2)}`;
}

export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, "");
  const dollars = parseFloat(cleaned) || 0;

  return Math.round(dollars * MINOR_UNITS_PER_UNIT);
}

export function loyaltyDiscountFor(
  points: number,
  redemptionRate: number,
  redemptionValue: number,
): number {
  if (points <= 0 || redemptionRate <= 0 || redemptionValue <= 0) {
    return 0;
  }

  return Math.round(
    (points / redemptionRate) * redemptionValue * MINOR_UNITS_PER_UNIT,
  );
}

export function loyaltyPointsEarnedFor(
  total: number,
  pointsPerUnit: number,
): number {
  if (total <= 0 || pointsPerUnit <= 0) {
    return 0;
  }

  return Math.floor((total / MINOR_UNITS_PER_UNIT) * pointsPerUnit);
}

export function maxRedeemablePointsFor(
  amount: number,
  redemptionRate: number,
  redemptionValue: number,
): number {
  if (amount <= 0 || redemptionRate <= 0 || redemptionValue <= 0) {
    return 0;
  }

  const perPoint = (redemptionValue * MINOR_UNITS_PER_UNIT) / redemptionRate;

  let points = Math.floor(amount / perPoint);

  while (
    points > 0 &&
    loyaltyDiscountFor(points, redemptionRate, redemptionValue) > amount
  ) {
    points -= 1;
  }

  return points;
}

const DAY_FIRST_LOCALE = "en-GB";

export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!date) {
    return "";
  }

  const d = typeof date === "string" ? new Date(date) : date;

  return d.toLocaleDateString(DAY_FIRST_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...options,
  });
}

export function formatTime(date: string | Date): string {
  if (!date) {
    return "";
  }

  const d = typeof date === "string" ? new Date(date) : date;

  return d.toLocaleTimeString(DAY_FIRST_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function formatDateTime(date: string | Date): string {
  if (!date) {
    return "";
  }

  const d = typeof date === "string" ? new Date(date) : date;

  return `${formatDate(d)} ${formatTime(d)}`;
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins === 1) return "1 minute ago";
  if (diffMins < 60) return `${diffMins} minutes ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours === 1) return "1 hour ago";
  if (diffHours < 24) return `${diffHours} hours ago`;

  return formatDate(d);
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;

  return `${text.slice(0, length)}...`;
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function generateId(): string {
  return crypto.randomUUID();
}

export function hasPermission(
  userPermissions: string[],
  requiredPermission: string,
): boolean {
  return userPermissions.includes(requiredPermission);
}

export function hasAnyPermission(
  userPermissions: string[],
  requiredPermissions: string[],
): boolean {
  return requiredPermissions.some((p) => userPermissions.includes(p));
}

export function hasAllPermissions(
  userPermissions: string[],
  requiredPermissions: string[],
): boolean {
  return requiredPermissions.every((p) => userPermissions.includes(p));
}

export function getOrderStatusVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "pending":
      return "secondary";
    case "preparing":
      return "default";
    case "ready":
      return "default";
    case "completed":
      return "outline";
    case "cancelled":
      return "destructive";
    default:
      return "secondary";
  }
}

export function calculateOrderTotal(
  subtotal: number,
  discountAmount: number = 0,
  tipAmount: number = 0,
): number {
  return subtotal - discountAmount + tipAmount;
}

export function calculateMarginPercent(
  price: number,
  cost: number,
): number | null {
  if (price <= 0) {
    return null;
  }

  return ((price - cost) / price) * 100;
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function slugify(
  text: string,
  options: {
    separator?: string;
    lowercase?: boolean;
    maxLength?: number;
  } = {},
): string {
  const { separator = "-", lowercase = true, maxLength = 50 } = options;
  const sep = escapeRegex(separator);

  let slug = text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, separator)
    .replace(new RegExp(`^${sep}+|${sep}+$`, "g"), "");

  if (lowercase) {
    slug = slug.toLowerCase();
  }

  if (maxLength && slug.length > maxLength) {
    slug = slug.slice(0, maxLength).replace(new RegExp(`${sep}+$`), "");
  }

  return slug;
}

export function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    const msg = err.message?.trim();
    if (msg) return msg;
  }
  if (typeof err === "string" && err.trim()) return err.trim();

  return fallback;
}

function fallbackSku(name: string): string {
  let hash = 0x811c9dc5;

  for (const char of name) {
    hash ^= char.codePointAt(0)!;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(36).toUpperCase().padStart(7, "0");
}

export function generateSku(name: string, prefix?: string): string {
  const trimmed = name.trim();

  if (!trimmed) {
    return "";
  }

  const slug = slugify(trimmed, {
    separator: "-",
    lowercase: false,
    maxLength: 30,
  }).toUpperCase();

  const base = slug || fallbackSku(trimmed);

  if (prefix) {
    return `${prefix.toUpperCase()}-${base}`;
  }

  return base;
}

export const SHOP_NAME = "Pessoa Coffee Shop";
